import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Grid } from '@mui/material';
import { HSpacer, RoutalLoader, RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';
import { QRCodeSVG } from 'qrcode.react';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import { Driver } from '../../../domain/driver/Driver';
import Text from '../../../react-components/Text';
import { useFinalizeOnboarding } from '../hooks/useFinalizeOnboarding';
import { CreateOrganizationOnboardingData } from '../hooks/useInitializeOnboarding';
import { useRetrieveVehicleHasSession } from '../hooks/useRetrieveVehicleHasSession';

interface QRContainerProps {
  isLoading: boolean;
}

const QRContainer = styled.div<QRContainerProps>`
  position: relative;
  width: 128px;
  height: 128px;
  background-color: ${RoutalPalette.neutral02n};
  border-radius: 12px;
  box-shadow: 0px 0px 8px rgba(125, 154, 150, 0.15);
  padding: 12px;

  ${({ isLoading }: QRContainerProps) => {
    if (!isLoading) return ``;

    // FYI - Source of the idea for the gradient border: https://codepen.io/shshaw/pen/RwJwJJx

    return `
      --border-angle: 0turn; // For animation.
      --main-bg: conic-gradient(
          from var(--border-angle),
          ${RoutalPalette.neutral02n},
          ${RoutalPalette.neutral02n} 5%,
          ${RoutalPalette.neutral02n} 60%,
          ${RoutalPalette.neutral02n} 95%
        );

      border: solid 4px transparent;

      border-radius: 12px;
      --gradient-border: conic-gradient(from var(--border-angle), transparent 25%, ${RoutalPalette.primary40n}, ${RoutalPalette.primary40} 99%, transparent);

      background: var(--main-bg) padding-box, var(--gradient-border) border-box, var(--main-bg) border-box;    
      background-position: center center;

      animation: bg-spin 2s linear infinite;

      @keyframes bg-spin {
        to {
          --border-angle: 1turn;
        }
      }

      @property --border-angle {
        syntax: "<angle>";
        inherits: true;
        initial-value: 0turn;
      }
    `;
  }}
`;

interface AccessDriversProps {
  onboardingData: CreateOrganizationOnboardingData;
  projectId: string;
}

export const AccessDrivers = ({ onboardingData, projectId }: AccessDriversProps) => {
  const { run: finalizeOnboarding } = useFinalizeOnboarding({ onboardingData, projectId });
  const [magicLink, setMagicLink] = useState<string | null>(null);
  const {
    error,
    success,
    isLoading,
    run: startLongPolling,
  } = useRetrieveVehicleHasSession({ vehicleId: onboardingData.vehicle_id });

  useEffect(() => {
    if (!magicLink) {
      const newMagicLink = Driver.generateRouteMagicLink(onboardingData.route_id, onboardingData.vehicle_id);
      setMagicLink(newMagicLink);
      startLongPolling();
    }
  }, [magicLink, onboardingData, startLongPolling]);

  useEffect(() => {
    if (!isLoading && success) {
      setTimeout(() => {
        finalizeOnboarding();
      }, 1800);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success]);

  if (magicLink === null) {
    return (
      <Grid container alignItems={`center`} flexDirection={`column`}>
        <RoutalLoader />
      </Grid>
    );
  }

  const hasError = error?.response?.data?.messageId === `highway.vehicle.error.not_found`;

  return (
    <Grid>
      <Grid display="flex" flexDirection="column" alignItems="center">
        <Grid display="flex" flexDirection="column" alignItems="center" width="60%">
          <Text variant="h6" intlId="company.onboarding.access_drivers.title" style={{ textAlign: `center` }} />

          <VSpacer small />

          <Text
            variant="body"
            intlId="company.onboarding.access_drivers.subtitle"
            style={{ textAlign: `center`, color: RoutalPalette.secondary00 }}
          />
        </Grid>

        <VSpacer large />

        <QRContainer isLoading={!hasError && (!success || isLoading)}>
          <QRCodeSVG
            value={magicLink}
            fgColor={RoutalPalette.secondary60}
            bgColor={RoutalPalette.neutral02n}
            level="L"
          />
        </QRContainer>
      </Grid>
      <VSpacer large />
      {hasError ? (
        <Grid container direction="column">
          <Grid item container direction="row" alignItems={`center`} justifyContent={`center`}>
            <CancelIcon style={{ color: RoutalPalette.error.medium, marginRight: `4px` }} />

            <Text
              variant="body"
              intlId="company.onboarding.access_drivers.error_message"
              style={{ color: RoutalPalette.error.medium }}
            />
          </Grid>

          <VSpacer medium />

          <Grid
            item
            container
            direction="row"
            alignItems={`center`}
            justifyContent={`center`}
            style={{
              cursor: `pointer`,
            }}
            onClick={() => window.location.reload()}
          >
            <RefreshIcon style={{ color: RoutalPalette.primary40, marginRight: `4px` }} />

            <Text
              variant="body"
              intlId="company.onboarding.access_drivers.refresh_page_button"
              style={{ color: RoutalPalette.primary40, cursor: `pointer` }}
            />
          </Grid>
        </Grid>
      ) : (
        <>
          {success ? (
            <Grid container direction="row" alignItems={`center`} justifyContent={`center`} height={`64px`}>
              <CheckCircleIcon style={{ color: RoutalPalette.success.medium }} />

              <HSpacer small />

              <Text
                variant="body"
                intlId="company.onboarding.access_drivers.success_message"
                style={{ color: RoutalPalette.success.medium }}
              />
            </Grid>
          ) : (
            <div style={{ height: `64px` }} />
          )}
        </>
      )}

      {/* TODO: CORRTECT MESSAGE AND ERROR MESSAGE */}

      {/* <VSpacer large />

      <Text
        variant="body"
        intlId="company.onboarding.access_drivers.image_subtitle"
        style={{ textAlign: `center`, color: RoutalPalette.primary80 }}
      /> */}

      {/* <Button
        intlId="company.onboarding.next"
        style={{
          width: `250px`,
          height: `35px`,
          padding: 0,
        }}
        onClick={onNextStep}
      /> */}
    </Grid>
  );
};
